import React, { useEffect, useState } from 'react';
import { ExternalLink, Info } from 'lucide-react';
import { ElementsRenderer, isWordDocument, UploadedFileViewer } from '../../shared';
import FileSelectInput from './FileSelectInput';
import useFileUpload from '../../../hooks/useFileUpload';
import { resolveDocumentUploadTypes, TEXT_TYPE } from '../../../constants';
import ReadingComprehensionElement from './ReadingComprehensionElement';
import { useAuth, useStableMediaUrl } from '../../../hooks';
import { getMediaBlob } from '../../../utils/mediaSessionCache';
import { viewUploadedFile } from '../../shared/uploadedFileActions';
import { Button } from '../../ui/button';

const FileUploadQuestionBody = ({
  elements,
  updateAndSaveAnswer,
  pauseStrictMode,
  resumeStrictMode,
  questionId,
  settings,
  uploadedFile,
  readingComprehensionQuestionRef
}) => {
  const { acceptedFileTypes, maxFileSize, fileUploadGuidelines } = settings || {};
  const { token } = useAuth();
  const resolvedAcceptedTypes = resolveDocumentUploadTypes(acceptedFileTypes);

  const handleFileSelected = (file) => {
    updateAndSaveAnswer(questionId, file);
  };

  const { fileInputRef, triggerFileSelect, handleFileSelect, selectedFile, handleFileRemoved, handleFilePickerCancel } =
    useFileUpload({
      pauseStrictMode,
      resumeStrictMode,
      acceptedFileTypes: resolvedAcceptedTypes,
      maxFileSize: maxFileSize || 10,
      onFileSelect: handleFileSelected,
      uploadedFile
    });

  const [localFileUrl, setLocalFileUrl] = useState(null);

  useEffect(() => {
    if (!(selectedFile instanceof File)) {
      setLocalFileUrl(null);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setLocalFileUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const uploadedFileKey = uploadedFile?.fileKey || null;
  const { mediaUrl } = useStableMediaUrl({
    blob: getMediaBlob({ fileKey: uploadedFileKey, questionId }) || null,
    fileKey: uploadedFileKey,
    questionId,
    token
  });

  const previewUrl = localFileUrl || mediaUrl || null;
  const previewName = selectedFile?.name || uploadedFile?.originalFileName || uploadedFileKey;
  const previewMime = (selectedFile instanceof File ? selectedFile.type : null) || uploadedFile?.mimeType;
  const isWordFile = isWordDocument(previewName, previewMime);

  const handleView = () =>
    viewUploadedFile({
      fileUrl: previewUrl,
      fileName: previewName,
      mimeType: previewMime
    });

  const guidelinesText =
    fileUploadGuidelines ||
    'Please ensure your file is in the accepted format and does not exceed the maximum file size.';

  return (
    <div className="mt-5 w-full min-w-0 space-y-4">
      {readingComprehensionQuestionRef && (
        <ReadingComprehensionElement questionRef={readingComprehensionQuestionRef} />
      )}

      <div className="katex-question min-w-0 space-y-2">
        <p className="text-sm font-semibold text-muted-foreground">Document Upload</p>
        <div className="question-container min-w-0 overflow-visible break-words text-base leading-relaxed text-foreground md:text-lg">
          <ElementsRenderer elements={elements} textType={TEXT_TYPE.TIPTAP} />
        </div>
      </div>

      {/* Guidelines above the drop zone so instructions aren't clipped under the mobile action bar */}
      <div className="flex min-w-0 gap-3 rounded-xl border border-sky-200 bg-sky-50 px-3 py-3 sm:px-4">
        <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-sky-600 text-white">
          <Info className="h-3 w-3" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <p className="text-xs font-semibold text-sky-900">Important Guidelines</p>
          <p className="whitespace-pre-line break-words text-xs leading-relaxed text-sky-800 [overflow-wrap:anywhere]">
            {guidelinesText}
          </p>
        </div>
      </div>

      <FileSelectInput
        handleFileRemoved={handleFileRemoved}
        fileInputRef={fileInputRef}
        triggerFileSelect={triggerFileSelect}
        selectedFile={selectedFile}
        acceptedFileTypes={resolvedAcceptedTypes}
        maxFileSize={maxFileSize || 10}
        handleFileSelect={handleFileSelect}
        handleFilePickerCancel={handleFilePickerCancel}
      />

      {previewUrl && isWordFile ? (
        <Button type="button" variant="outline" size="sm" onClick={handleView} className="bg-white">
          <ExternalLink className="h-4 w-4" />
          View
        </Button>
      ) : null}

      {previewUrl && !isWordFile ? (
        <UploadedFileViewer fileUrl={previewUrl} fileName={previewName} mimeType={previewMime} />
      ) : null}
    </div>
  );
};

export default FileUploadQuestionBody;
